// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import VariantParentProductPrice from "@eisinc/App/Components/Price/VariantParentProductPrice";
import ProductActions, { ProductActionsStyle } from "@eisinc/App/Components/Product/ProductActions";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import Button from "@eisinc/MobiusOverrides/Button";
import Checkbox, { CheckboxPresentationProps } from "@eisinc/MobiusOverrides/Checkbox/Checkbox";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@eisinc/Overrides/Components/ProductAddToListLink";
import { HasIsLoading } from "@eisinc/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";

import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // XNG-Changes
    showCompare?: boolean;
    showActions?: boolean;
}

// XNG-Changes : add props
const mapStateToProps = (state: ApplicationState, props: HasProductContext) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        productSettings: settingsCollection.productSettings,
        // XNG-Changes
        productCompareChecked: getProductCompareChecked(state, props.productContext),
        isProductCompareFull: getIsProductCompareFull(state),
    };
};

const mapDispatchToProps = {
    // XNG-Changes
    addProductToCompare,
    removeProductFromCompare,
};
type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps &
    HasIsLoading;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // XNG-Changes
    compareCheckbox?: CheckboxPresentationProps;
    productActions?: ProductActionsStyle;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-top: 10px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            min-height: 2rem;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    // XNG-Changes
    showCompare,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,
    isProductCompareFull,
    isLoading,
    showActions,
}) => {
    // <XNG-Changes>
    if (isLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {showPrice && <Skeleton height="24px" width="100%" />}
                {showActions && (
                    <StyledWrapper {...styles.productActions?.wrapper}>
                        <Skeleton height="26px" width="94px" />
                        <Skeleton height="26px" width="100%" />
                    </StyledWrapper>
                )}
                {(showAddToList || showCompare) && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <Skeleton height="14px" width="100%" />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    }
    const toasterContext = useContext(ToasterContext);

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    // </XNG-Changes>

    return (
        <StyledWrapper {...styles.wrapper}>
            {/* <XNG-Changes> */}
            {showPrice && productContext.product?.isVariantParent && <VariantParentProductPrice />}
            {/* </XNG-Changes> */}

            {showPrice && !productContext.product?.isVariantParent && (
                <ProductPrice
                    product={productContext}
                    showLabel={false}
                    showSavings={true}
                    showSavingsAmount={productSettings.showSavingsAmount}
                    showSavingsPercent={productSettings.showSavingsPercent}
                    extendedStyles={styles.price}
                />
            )}

            {/* <XNG-Changes> */}
            {showActions && <ProductActions extendedStyles={styles.productActions} />}
            {/* </XNG-Changes> */}

            {/* <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                <ProductAddToCartButton
                    data-test-selector={`actionsAddToCart${productContext.product.id}`}
                    extendedStyles={styles.addToCartButton}
                />
            </StyledWrapper> */}
            {(showAddToList || showCompare) && (
                <StyledWrapper {...styles.addToListWrapper}>
                    {showAddToList && (
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                            extendedStyles={styles.addToListLink}
                            // XNG-Changes
                            {...{ showIcon: true }}
                        />
                    )}
                    {showCompare && productSettings.enableProductComparisons && (
                        <Checkbox
                            {...styles.compareCheckbox}
                            checked={productCompareChecked}
                            onChange={handleChanged}
                            data-test-selector="productList_CompareCheckbox"
                        >
                            {translate("Compare")}
                        </Checkbox>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions));
